import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';

import { FormField } from '../patterns/FormField';
import { rem } from '../tokens/spacing';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

const StyledQuillWrapper = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  .ql-toolbar.ql-snow {
    border-color: ${({ $hasError }) =>
      $hasError
        ? 'var(--ui-status-danger, #d32f2f)'
        : 'var(--ui-input-border, var(--ui-border, #d9d9d9))'};
    border-top-left-radius: var(--ui-radius-md, ${rem(6)});
    border-top-right-radius: var(--ui-radius-md, ${rem(6)});
    background: ${({ $disabled }) =>
      $disabled ? 'var(--ui-background-muted, #F3F3F3)' : 'var(--ui-surface, #ffffff)'};
    font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
    ${({ $disabled }) => $disabled && 'pointer-events: none; opacity: 1;'}
  }

  .ql-container.ql-snow {
    border-color: ${({ $hasError }) =>
      $hasError
        ? 'var(--ui-status-danger, #d32f2f)'
        : 'var(--ui-input-border, var(--ui-border, #d9d9d9))'};
    border-bottom-left-radius: var(--ui-radius-md, ${rem(6)});
    border-bottom-right-radius: var(--ui-radius-md, ${rem(6)});
    background: ${({ $disabled }) =>
      $disabled ? 'var(--ui-background-muted, #F3F3F3)' : 'var(--ui-surface, #ffffff)'};
    font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
    font-size: ${rem(15)};
    color: var(--ui-text, #161616);
    min-height: ${rem(120)};
  }

  .ql-editor {
    min-height: ${rem(100)};
    padding: ${rem(12)} ${rem(16)};
  }

  .ql-editor.ql-blank::before {
    color: var(--ui-text-subtle, #767676);
    font-style: normal;
    font-size: ${rem(15)};
  }
`;

const DEFAULT_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
  ],
};

const DEFAULT_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet'];

export interface RhfRichTextProps extends RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Quill modules config — defaults to bold/italic/underline + ordered/bullet list. */
  modules?: Record<string, unknown>;
  /** Quill formats whitelist — defaults to ['bold', 'italic', 'underline', 'list', 'bullet']. */
  formats?: string[];
}

export const RhfRichText = React.forwardRef<HTMLDivElement, RhfRichTextProps>(
  function RhfRichText(
    {
      control,
      name,
      label,
      hint,
      tooltip,
      required = false,
      semanticId,
      disabled = false,
      placeholder = 'Enter text…',
      modules = DEFAULT_MODULES,
      formats = DEFAULT_FORMATS,
      controlWidth,
    },
    ref,
  ) {
    const stableModules = React.useMemo(() => modules, []);
    const stableFormats = React.useMemo(() => formats, []);

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });

          return (
            <FormField
              ref={ref}
              label={label}
              hint={hint}
              tooltip={tooltip}
              required={required}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
            >
              <StyledQuillWrapper $hasError={Boolean(errorText)} $disabled={disabled}>
                <ReactQuill
                  value={field.value ?? ''}
                  onChange={(content) => {
                    field.onChange(content);
                  }}
                  onBlur={() => field.onBlur()}
                  modules={stableModules}
                  formats={stableFormats}
                  placeholder={placeholder}
                  readOnly={disabled}
                  preserveWhitespace
                />
              </StyledQuillWrapper>
            </FormField>
          );
        }}
      />
    );
  },
);

RhfRichText.displayName = 'RhfRichText';
